import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, LayoutGrid, List, Check, ArrowDownUp } from 'lucide-react';
import FilterDropdown from './FilterDropdown';

const SORT_OPTIONS = [
  { key: 'updated_at', label: 'Last Activity' },
  { key: 'created_at', label: 'Date Created' },
  { key: 'name', label: 'Title' },
  { key: 'submissions', label: 'Submissions' },
];

const SortDropdown = ({ sort, setSort }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const current = SORT_OPTIONS.find((o) => o.key === sort.field) || SORT_OPTIONS[0];
  return (
    <div className="relative" ref={ref}>
      <button data-testid="sort-button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#D5D7ED] text-[14px] text-[#0A1551] hover:bg-[#F5F6FB] transition-colors font-medium">
        <span>{current.label}</span>
        <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div data-testid="sort-dropdown" className="absolute left-0 top-full mt-2 z-40 w-52 bg-white border border-[#E3E5F5] rounded-xl shadow-xl py-1">
          {SORT_OPTIONS.map((o) => (
            <button key={o.key} data-testid={`sort-${o.key}`} onClick={() => { setSort((s) => ({ ...s, field: o.key })); setOpen(false); }}
              className="w-full flex items-center justify-between px-3 py-2 text-[13px] text-[#0A1551] hover:bg-[#F5F6FB]">
              {o.label} {sort.field === o.key && <Check className="w-3.5 h-3.5 text-[#0F766E]" />}
            </button>
          ))}
          <div className="my-1 border-t border-[#E3E5F5]" />
          <button data-testid="sort-order-toggle" onClick={() => setSort((s) => ({ ...s, order: s.order === 'asc' ? 'desc' : 'asc' }))}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[#6F76A7] hover:bg-[#F5F6FB]">
            <ArrowDownUp className="w-3.5 h-3.5" /> {sort.order === 'asc' ? 'Ascending' : 'Descending'}
          </button>
        </div>
      )}
    </div>
  );
};

const Toolbar = ({ view, setView, search, setSearch, filters, setFilters, sort, setSort, teams, hideTeamFilter }) => {
  const [filterOpen, setFilterOpen] = useState(false);
  return (
    <div className="min-h-[60px] px-3 md:px-6 py-2 md:py-0 border-b border-[#E3E5F5] bg-white flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-3">
      <FilterDropdown open={filterOpen} setOpen={setFilterOpen} filters={filters} setFilters={setFilters} teams={teams} hideTeam={hideTeamFilter} />
      <SortDropdown sort={sort} setSort={setSort} />

      <div className="ml-auto flex items-center gap-3 w-full md:w-auto order-last md:order-none">
        <div className="hidden sm:flex items-center border border-[#D5D7ED] rounded-md p-0.5">
          <button onClick={() => setView('grid')} data-testid="view-grid" className={`p-1.5 rounded ${view === 'grid' ? 'bg-[#F0F1FB] text-[#0A1551]' : 'text-[#6F76A7] hover:text-[#0A1551]'}`} aria-label="Grid view">
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button onClick={() => setView('list')} data-testid="view-list" className={`p-1.5 rounded ${view === 'list' ? 'bg-[#F0F1FB] text-[#0A1551]' : 'text-[#6F76A7] hover:text-[#0A1551]'}`} aria-label="List view">
            <List className="w-4 h-4" />
          </button>
        </div>
        <div className="relative flex-1 md:flex-none">
          <Search className="w-4 h-4 text-[#6F76A7] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="text"
            data-testid="search-input"
            placeholder="Search"
            className="pl-9 pr-4 py-2 w-full md:w-[260px] border border-[#D5D7ED] rounded-md text-[14px] text-[#0A1551] placeholder:text-[#9599C0] focus:outline-none focus:border-[#0A1551] focus:ring-2 focus:ring-[#0A1551]/10 transition-colors"
          />
        </div>
      </div>
    </div>
  );
};

export default Toolbar;
